import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fromScaled, toScaled, type FpaQueueBlock, type FpaQueueStatus } from '@loot/shared';
import { api } from '../../api.ts';
import { FormModal, FormSection } from '../../components/FormModal.tsx';
import { Tabs } from '../../components/Tabs.tsx';
import {
  Badge,
  EmptyRow,
  ErrorBox,
  Field,
  Input,
  Loading,
  NumCell,
  PageHeader,
  Panel,
  Table,
  Textarea,
} from '../../components/ui.tsx';
import { useFormat } from '../purchasing/common.tsx';

const KEY = ['controlling', 'actual-queue'];
const TABS = ['pending', 'sheet', 'refused', 'registered', 'withdrawn', 'periods'] as const;
type Tab = (typeof TABS)[number];

interface QueueItem {
  id: string;
  kind: 'payment' | 'reversal';
  status: FpaQueueStatus;
  competence: string;
  currency: string;
  paidNet: string;
  recognized: string;
  withheld: string;
  financial: string;
  decisionNote: string | null;
  entityCode: string | null;
  payableCode: string | null;
  description: string | null;
  partnerName: string | null;
  documentNumber: string | null;
  occurredOn: string | null;
  conciliated: boolean;
  netsuite: string | null;
  expenseTypeName: string | null;
  classificationName: string | null;
  blocks: FpaQueueBlock[];
}

interface QueueLine {
  costCenterName?: string | null;
  costCenterId: string;
  employeeName: string | null;
  amount: string;
  paidNet: string;
  withheld: string;
  financial: string;
  budget: {
    status: 'active' | 'superseded' | 'undone';
    versionCode: string;
    expense: string;
  } | null;
}

interface QueueDetail {
  item: QueueItem & { decidedAt: string | null; decidedByName: string | null };
  blocks: FpaQueueBlock[];
  inResult: boolean;
  trail: {
    payableCode: string;
    description: string;
    partnerName: string | null;
    documentType: string | null;
    documentNumber: string | null;
    originKind: string;
    orderCode: string | null;
    invoiceCode: string | null;
    grossAmount: string;
    netAmount: string;
    occurredOn: string;
    reference: string | null;
    netsuitePayable: string | null;
    netsuitePayment: string | null;
    conciliated: boolean;
  };
  lines: QueueLine[];
  postings: (QueueLine & { amountBrl: string })[];
  openWithholdings: {
    withholdingId: string;
    payableCode: string;
    taxCode: string;
    amount: string;
    settled: string;
    recognized: boolean;
    competence: string;
  }[];
}

interface Period {
  legalEntityId: string;
  entityCode: string | null;
  month: string;
  pendingItems: number;
  openTitles: number;
  registeredBrl: string;
  state: 'partial' | 'registered';
}

const SHEET_BLOCKS: FpaQueueBlock[] = ['sheet_competence', 'sheet_origin'];
const isSheet = (i: { blocks: FpaQueueBlock[] }) => i.blocks.some((b) => SHEET_BLOCKS.includes(b));

/**
 * Fila do Realizado (ADR 0098): cada pagamento registrado espera aqui. Estar na fila não muda o
 * Realizado; só o Registrar, depois de conferir competência, classificação e rateio.
 */
export function ActualQueuePage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>('pending');
  const [openId, setOpenId] = useState<string | null>(null);
  return (
    <div className="flex flex-col gap-6">
      <PageHeader title={t('nav.controllingQueue')} description={t('controlling.queue.about')} />
      <Tabs
        items={TABS.map((id) => ({ id, label: t(`controlling.queue.tabs.${id}`) }))}
        value={tab}
        onChange={setTab}
        label={t('nav.controllingQueue')}
        idPrefix="actual-queue"
      />
      <div role="tabpanel" id={`actual-queue-panel-${tab}`}>
        {tab === 'periods' ? <Periods /> : <ItemList tab={tab} onOpen={setOpenId} />}
      </div>
      {openId && <ItemModal id={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
}

const STATUS_OF: Record<Exclude<Tab, 'periods'>, FpaQueueStatus[]> = {
  pending: ['pending'],
  sheet: ['pending', 'sheet_checked'],
  refused: ['refused'],
  registered: ['registered'],
  withdrawn: ['withdrawn'],
};

function ItemList({ tab, onOpen }: { tab: Exclude<Tab, 'periods'>; onOpen: (id: string) => void }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const statuses = STATUS_OF[tab];
  const data = useQuery({
    queryKey: [...KEY, 'list', tab],
    queryFn: async () => {
      const lists = await Promise.all(
        statuses.map((s) => api.get<{ items: QueueItem[] }>(`/fpa/actual-queue?status=${s}`)),
      );
      return lists.flatMap((l) => l.items);
    },
  });
  if (data.isPending) return <Loading />;
  if (data.isError) return <ErrorBox error={data.error} />;
  const items = data.data.filter((i) =>
    tab === 'pending' ? !isSheet(i) : tab === 'sheet' ? isSheet(i) || i.status !== 'pending' : true,
  );
  return (
    <Table
      head={[
        t('controlling.queue.fields.competence'),
        t('controlling.queue.fields.title'),
        t('controlling.queue.fields.classification'),
        { label: t('controlling.queue.fields.expense'), align: 'right' },
        { label: t('controlling.queue.fields.paid'), align: 'right' },
        t('controlling.queue.fields.reconciled'),
        t('controlling.queue.fields.netsuite'),
        t('controlling.queue.fields.reason'),
      ]}
    >
      {items.length === 0 && <EmptyRow colSpan={8}>{t('controlling.queue.empty')}</EmptyRow>}
      {items.map((i) => (
        <tr key={i.id} className="border-b border-rule last:border-0">
          <td className="px-4 py-2.5">
            {fmt.month(i.competence)}
            <span className="block text-xs text-muted">{i.entityCode}</span>
          </td>
          <td className="px-4 py-2.5">
            <button
              type="button"
              className="text-left font-medium text-brand-text underline-offset-2 hover:underline"
              onClick={() => onOpen(i.id)}
            >
              {i.payableCode}
            </button>
            {i.kind === 'reversal' && (
              <span className="ml-2">
                <Badge tone="off">{t('controlling.queue.reversal')}</Badge>
              </span>
            )}
            <span className="block text-xs text-muted">{i.partnerName ?? i.description}</span>
          </td>
          <td className="px-4 py-2.5">
            {i.expenseTypeName ?? '—'}
            <span className="block text-xs text-muted">{i.classificationName ?? '—'}</span>
          </td>
          <NumCell>{fmt.money(i.recognized, i.currency)}</NumCell>
          <NumCell>{fmt.money(i.paidNet, i.currency)}</NumCell>
          <td className="px-4 py-2.5">
            <Badge tone={i.conciliated ? 'ok' : 'neutral'}>
              {t(`controlling.queue.reconciled.${i.conciliated ? 'yes' : 'no'}`)}
            </Badge>
          </td>
          <td className="px-4 py-2.5">{i.netsuite ?? '—'}</td>
          <td className="px-4 py-2.5 text-xs text-muted">
            {i.status === 'pending'
              ? i.blocks.map((b) => t(`controlling.queue.blocks.${b}`)).join(' ')
              : (i.decisionNote ?? '')}
          </td>
        </tr>
      ))}
    </Table>
  );
}

function Periods() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const [year, setYear] = useState(() => String(new Date().getUTCFullYear()));
  const data = useQuery({
    queryKey: [...KEY, 'periods', year],
    queryFn: () => api.get<{ items: Period[] }>(`/fpa/actual-queue/periods?year=${year}`),
  });
  if (data.isPending) return <Loading />;
  if (data.isError) return <ErrorBox error={data.error} />;
  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-muted">{t('controlling.queue.periodsAbout')}</p>
      <div className="w-32">
        <Field label={t('controlling.queue.fields.year')}>
          <Input
            id="periods-year"
            value={year}
            inputMode="numeric"
            onChange={(e) => setYear(e.target.value)}
          />
        </Field>
      </div>
      <Table
        head={[
          t('controlling.queue.fields.competence'),
          t('controlling.queue.fields.entity'),
          t('controlling.queue.fields.state'),
          { label: t('controlling.queue.fields.pendingItems'), align: 'right' },
          { label: t('controlling.queue.fields.openTitles'), align: 'right' },
          { label: t('controlling.queue.fields.registered'), align: 'right' },
        ]}
      >
        {data.data.items.length === 0 && (
          <EmptyRow colSpan={6}>{t('controlling.queue.noPeriods')}</EmptyRow>
        )}
        {data.data.items.map((p) => (
          <tr key={`${p.legalEntityId}-${p.month}`} className="border-b border-rule last:border-0">
            <td className="px-4 py-2.5">{fmt.month(p.month)}</td>
            <td className="px-4 py-2.5">{p.entityCode}</td>
            <td className="px-4 py-2.5">
              <Badge tone={p.state === 'partial' ? 'accent' : 'ok'}>
                {t(`controlling.queue.periodState.${p.state}`)}
              </Badge>
            </td>
            <NumCell>{p.pendingItems}</NumCell>
            <NumCell>{p.openTitles}</NumCell>
            <NumCell>{fmt.money(p.registeredBrl, 'BRL')}</NumCell>
          </tr>
        ))}
      </Table>
    </div>
  );
}

type Decision = 'register' | 'refuse' | 'confirm-sheet' | 'reopen';

function ItemModal({ id, onClose }: { id: string; onClose: () => void }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const qc = useQueryClient();
  const [checks, setChecks] = useState({
    competence: false,
    classification: false,
    allocation: false,
  });
  const [note, setNote] = useState('');
  const [decision, setDecision] = useState<Decision | null>(null);
  const [tried, setTried] = useState(false);
  const [settlements, setSettlements] = useState<Record<string, string>>({});
  const data = useQuery({
    queryKey: [...KEY, 'detail', id],
    queryFn: () => api.get<QueueDetail>(`/fpa/actual-queue/${id}`),
  });
  const act = useMutation({
    mutationFn: (kind: Decision) =>
      api.post(
        `/fpa/actual-queue/${id}/${kind}`,
        kind === 'register'
          ? {
              checks,
              ...(note.trim() ? { note: note.trim() } : {}),
              settlements: Object.entries(settlements)
                .filter(([, amount]) => amount.trim())
                .map(([withholdingId, amount]) => ({ withholdingId, amount: amount.trim() })),
            }
          : { note: note.trim() },
      ),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: KEY });
      onClose();
    },
  });
  const d = data.data;
  const title = d ? `${d.trail.payableCode} · ${fmt.month(d.item.competence)}` : '';
  const money = (v: string) => fmt.money(v, d?.item.currency ?? 'BRL');
  const pending = d?.item.status === 'pending';
  const sheet = !!d && isSheet(d);
  const canRegister = pending && d.blocks.length === 0;
  const allChecked = checks.competence && checks.classification && checks.allocation;
  const settled = Object.values(settlements).reduce(
    (s, v) => s + (/^\d+(\.\d{1,4})?$/.test(v.trim()) ? toScaled(v.trim()) : 0n),
    0n,
  );
  const needsNote = decision !== 'register' && !!decision;
  const save = () => {
    if (decision) act.mutate(decision);
  };
  return (
    <FormModal
      open
      title={title}
      description={d?.trail.description}
      size="xl"
      dirty={!!note || allChecked}
      saving={act.isPending}
      error={act.error}
      readOnly={!decision}
      saveLabel={decision ? t(`controlling.queue.actions.${decision}`) : undefined}
      saveTone={decision === 'refuse' ? 'danger' : 'primary'}
      requiredLegend={false}
      onSave={() => {
        if (decision === 'register' ? allChecked : note.trim().length >= 3) save();
        else setTried(true);
      }}
      onClose={onClose}
    >
      {data.isPending && <Loading />}
      {data.isError && <ErrorBox error={data.error} />}
      {d && (
        <div className="col-span-full flex flex-col gap-5">
          <FormSection title={t('controlling.queue.detail.trail')}>
            <dl className="col-span-full grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
              {(
                [
                  ['partner', d.trail.partnerName ?? '—'],
                  ['document', `${d.trail.documentType ?? ''} ${d.trail.documentNumber ?? '—'}`],
                  ['order', d.trail.orderCode ?? '—'],
                  ['invoice', d.trail.invoiceCode ?? '—'],
                  ['gross', money(d.trail.grossAmount)],
                  ['net', money(d.trail.netAmount)],
                  ['paidOn', fmt.date(d.trail.occurredOn)],
                  ['reference', d.trail.reference ?? '—'],
                  [
                    'classification',
                    `${d.item.expenseTypeName ?? '—'} · ${d.item.classificationName ?? '—'}`,
                  ],
                  ['netsuite', d.trail.netsuitePayment ?? d.trail.netsuitePayable ?? '—'],
                  [
                    'reconciled',
                    t(`controlling.queue.reconciled.${d.trail.conciliated ? 'yes' : 'no'}`),
                  ],
                  ['ledger', t('controlling.queue.detail.ledgerNote')],
                ] as const
              ).map(([k, v]) => (
                <div key={k}>
                  <dt className="text-xs text-muted">{t(`controlling.queue.detail.${k}`)}</dt>
                  <dd>{v}</dd>
                </div>
              ))}
            </dl>
          </FormSection>
          <FormSection title={t('controlling.queue.detail.values')}>
            <dl className="col-span-full grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
              {(
                [
                  ['expense', d.item.recognized],
                  ['paid', d.item.paidNet],
                  ['withheld', d.item.withheld],
                  ['financial', d.item.financial],
                ] as const
              ).map(([k, v]) => (
                <div key={k}>
                  <dt className="text-xs text-muted">{t(`controlling.queue.fields.${k}`)}</dt>
                  <dd className="tabular-nums">{money(v)}</dd>
                </div>
              ))}
            </dl>
            {!d.inResult && (
              <p className="col-span-full text-sm text-muted">
                {t('controlling.queue.detail.outsideResult')}
              </p>
            )}
          </FormSection>
          <FormSection title={t('controlling.queue.detail.allocation')}>
            <div className="col-span-full">
              <Table
                head={[
                  t('controlling.queue.fields.costCenter'),
                  t('controlling.queue.fields.employee'),
                  t('controlling.queue.fields.budget'),
                  { label: t('controlling.queue.fields.expense'), align: 'right' },
                  { label: t('controlling.queue.fields.paid'), align: 'right' },
                  { label: t('controlling.queue.fields.withheld'), align: 'right' },
                ]}
              >
                {(d.postings.length ? d.postings : d.lines).map((l, i) => (
                  <tr key={i} className="border-b border-rule last:border-0">
                    <td className="px-4 py-2">{l.costCenterName ?? l.costCenterId}</td>
                    <td className="px-4 py-2">{l.employeeName ?? '—'}</td>
                    <td className="px-4 py-2">
                      {l.budget ? (
                        <>
                          {l.budget.expense}
                          <span className="block text-xs text-muted">
                            {l.budget.versionCode}
                            {l.budget.status !== 'active' &&
                              ` · ${t(`controlling.queue.budget.${l.budget.status}`)}`}
                          </span>
                        </>
                      ) : (
                        t('controlling.queue.budget.none')
                      )}
                    </td>
                    <NumCell>{money(l.amount)}</NumCell>
                    <NumCell>{money(l.paidNet)}</NumCell>
                    <NumCell>{money(l.withheld)}</NumCell>
                  </tr>
                ))}
              </Table>
            </div>
          </FormSection>
          {d.item.status !== 'pending' && (
            <Panel>
              <p className="text-sm">
                {t(`controlling.queue.status.${d.item.status}`)}
                {d.item.decidedByName ? ` · ${d.item.decidedByName}` : ''}
                {d.item.decidedAt ? ` · ${fmt.dateTime(d.item.decidedAt)}` : ''}
              </p>
              {d.item.decisionNote && (
                <p className="mt-1 text-sm text-muted">{d.item.decisionNote}</p>
              )}
            </Panel>
          )}
          {pending && d.blocks.length > 0 && (
            <div role="status" className="rounded-lg border border-danger bg-danger/5 p-3 text-sm">
              {d.blocks.map((b) => (
                <p key={b}>{t(`controlling.queue.blocks.${b}`)}</p>
              ))}
            </div>
          )}
          {pending && (
            <FormSection title={t('controlling.queue.detail.decision')}>
              <div className="col-span-full flex flex-wrap gap-4 text-sm">
                {(
                  [
                    ...(canRegister ? (['register'] as const) : []),
                    ...(sheet ? (['confirm-sheet'] as const) : []),
                    'refuse',
                  ] as Decision[]
                ).map((k) => (
                  <label key={k} className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="queue-decision"
                      checked={decision === k}
                      onChange={() => setDecision(k)}
                    />
                    {t(`controlling.queue.actions.${k}`)}
                  </label>
                ))}
              </div>
              {decision === 'register' && (
                <div className="col-span-full flex flex-col gap-2 text-sm">
                  {(['competence', 'classification', 'allocation'] as const).map((k) => (
                    <label key={k} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={checks[k]}
                        onChange={(e) => setChecks({ ...checks, [k]: e.target.checked })}
                      />
                      {t(`controlling.queue.checks.${k}`)}
                    </label>
                  ))}
                  {!allChecked && (
                    <p className="text-muted">{t('controlling.queue.checksRequired')}</p>
                  )}
                </div>
              )}
              {decision === 'register' && d.item.kind === 'payment' && (
                <div className="col-span-full text-sm">
                  <details>
                    <summary className="cursor-pointer">
                      {t('controlling.queue.settlement.title')}
                    </summary>
                    <p className="mt-2 text-muted">{t('controlling.queue.settlement.about')}</p>
                    <div className="mt-2 flex flex-col gap-2">
                      {d.openWithholdings.length === 0 && (
                        <p className="text-muted">{t('controlling.queue.settlement.empty')}</p>
                      )}
                      {d.openWithholdings.map((w) => (
                        <label key={w.withholdingId} className="flex flex-wrap items-center gap-2">
                          <span className="min-w-56">
                            {w.payableCode} · {w.taxCode} · {fmt.month(w.competence)} ·{' '}
                            {money(fromScaled(toScaled(w.amount) - toScaled(w.settled)))}
                          </span>
                          <Input
                            aria-label={t('controlling.queue.settlement.amount')}
                            className="w-32"
                            disabled={!w.recognized}
                            value={settlements[w.withholdingId] ?? ''}
                            onChange={(e) =>
                              setSettlements({
                                ...settlements,
                                [w.withholdingId]: e.target.value,
                              })
                            }
                          />
                          {!w.recognized && (
                            <span className="text-xs text-muted">
                              {t('controlling.queue.settlement.notRecognized')}
                            </span>
                          )}
                        </label>
                      ))}
                      {settled > 0n && (
                        <p>
                          {t('controlling.queue.settlement.remaining', {
                            amount: money(fromScaled(toScaled(d.item.recognized) - settled)),
                          })}
                        </p>
                      )}
                    </div>
                  </details>
                </div>
              )}
              {decision && (
                <Field
                  className="col-span-full"
                  label={t('controlling.queue.fields.note')}
                  required={needsNote}
                  error={
                    tried && needsNote && note.trim().length < 3
                      ? t('controlling.queue.noteRequired')
                      : undefined
                  }
                >
                  <Textarea
                    id="queue-note"
                    rows={2}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />
                </Field>
              )}
            </FormSection>
          )}
          {d.item.status === 'refused' && (
            <FormSection title={t('controlling.queue.detail.decision')}>
              <div className="col-span-full text-sm">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="queue-decision"
                    checked={decision === 'reopen'}
                    onChange={() => setDecision('reopen')}
                  />
                  {t('controlling.queue.actions.reopen')}
                </label>
              </div>
              {decision === 'reopen' && (
                <Field
                  className="col-span-full"
                  label={t('controlling.queue.fields.note')}
                  error={
                    tried && note.trim().length < 3
                      ? t('controlling.queue.noteRequired')
                      : undefined
                  }
                  required
                >
                  <Textarea
                    id="queue-note"
                    rows={2}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />
                </Field>
              )}
            </FormSection>
          )}
        </div>
      )}
    </FormModal>
  );
}
